<script>
import { computed, ref, onMounted, onUnmounted } from 'vue';
import { useStore } from 'vuex';

export default {
  props: {
    modelValue: Boolean,
    rail: Boolean,
    isMobile: Boolean,
  },
  emits: ['update:modelValue', 'update:rail'],
  setup(props, { emit }) {
    const store = useStore();
    const navRef = ref(null);
    const hasSpecialFileAccess = computed(() => store.state.hasSpecialFileAccess);
    const permissions = computed(() => store.state.authentication.permissions || []);
    const isBackendAdmin = computed(() => store.state.authentication.is_admin);

    const hasAdminAccess = (perms, is_admin_flag) => {
        const isAdminPermission = perms.includes('admin_only');
        return isAdminPermission && is_admin_flag;
    };

    const isAdmin = computed(() => hasAdminAccess(permissions.value, isBackendAdmin.value));
    const isPending = computed(() => store.state.authentication.account_status === 'Pending');

    const hasSdkAccess = computed(() => permissions.value.includes('sdk_access'));
    const isCaptureOnly = computed(() => store.state.authentication.customer_type === 'capture');

    // Capture is sold independently of the SDK, so it gates on its own scopes only.
    const hasCaptureKeys = computed(() => permissions.value.includes('capture:keys:read'));
    const hasCaptureSeats = computed(() => permissions.value.includes('capture:seats:read'));
    const hasCaptureAccess = computed(() => hasCaptureKeys.value || hasCaptureSeats.value);
    const isOrgMember = computed(() => store.getters.isOrgMember);

    const handleItemClick = () => {
        // On mobile, when an item is clicked, expand the menu (turn off rail mode)
        if (props.isMobile && props.rail) {
            emit('update:rail', false);
        }
    };

    const handleOutsideClick = (event) => {
        // On mobile, when clicking outside and menu is expanded, collapse it back to rail mode
        if (props.isMobile && !props.rail && navRef.value) {
            const drawerElement = navRef.value.$el || navRef.value;

            // Check if click is outside the navigation drawer
            if (!drawerElement.contains(event.target)) {
                emit('update:rail', true);
            }
        }
    };

    onMounted(() => {
        // Use capture phase to ensure we catch the event before other handlers
        document.addEventListener('click', handleOutsideClick, true);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleOutsideClick, true);
    });

    return {
        hasSpecialFileAccess,
        hasSdkAccess,
        isCaptureOnly,
        hasCaptureKeys,
        hasCaptureSeats,
        hasCaptureAccess,
        isAdmin,
        isPending,
        isOrgMember,
        handleItemClick,
        navRef
    };
  }
};
</script>

<template>
    <v-navigation-drawer
        :model-value="true"
        :rail="rail"
        :permanent="true"
        ref="navRef"
    >
        <v-list nav class="mt-2" @click="handleItemClick">
        <v-list-item id="nav-account" :to="{ path: '/console/account' }" prepend-icon="fas fa-user" title="Account" value="account"></v-list-item>
        <template v-if="!isPending">
            <v-list-item v-if="!isCaptureOnly" id="nav-getstarted" :to="{ path: '/console/getstarted' }" prepend-icon="fas fa-clipboard-check" title="Get Started" value="getstarted"></v-list-item>
            <v-list-item v-if="hasSdkAccess" id="nav-sdkkeys" :to="{ path: '/console/vault/sdk-keys' }" prepend-icon="fas fa-key" title="SDK Keys" value="sdk-keys"></v-list-item>
            <v-list-group v-if="hasSdkAccess" value="downloads" id="nav-downloads">
                <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" prepend-icon="fas fa-download" title="Downloads"></v-list-item>
                </template>
                <v-list-item id="nav-sdk-ios" :to="{ path: '/console/downloads/ios-sdk' }" title="Structure SDK for iOS" value="iossdk"></v-list-item>
                <v-list-item id="nav-sdk-xp" :to="{ path: '/console/downloads/xp-sdk' }" title="Structure SDK (Cross Platform)" value="xpsdk"></v-list-item>
                <v-list-item v-if="hasSpecialFileAccess" id="nav-special-files" :to="{ path: '/console/downloads/special-files' }" title="Special Files" value="special-files"></v-list-item>
            </v-list-group>
            <v-list-item v-if="hasSdkAccess" id="nav-usage" :to="{ path: '/console/usage' }" prepend-icon="fas fa-chart-line" title="Usage" value="usage"></v-list-item>
            <v-list-item v-if="isOrgMember" id="nav-organization" :to="{ path: '/console/organization' }" prepend-icon="fas fa-sitemap" title="Organization" value="organization"></v-list-item>
            <v-list-group v-if="hasCaptureAccess" value="capture" id="nav-capture">
                <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" prepend-icon="fas fa-cube" title="Structure Capture"></v-list-item>
                </template>
                <v-list-item v-if="hasCaptureKeys" id="nav-capture-keys" :to="{ path: '/console/capture/keys' }" title="Activation Keys" value="capture-keys"></v-list-item>
                <v-list-item v-if="hasCaptureSeats" id="nav-capture-seats" :to="{ path: '/console/capture/seats' }" title="Active Seats" value="capture-seats"></v-list-item>
            </v-list-group>
            <v-list-group v-if="isAdmin" value="admin" id="nav-admin">
                <template v-slot:activator="{ props }">
                <v-list-item v-bind="props" prepend-icon="fas fa-user-shield" title="Admin"></v-list-item>
                </template>
                <v-list-item id="nav-admin-users" :to="{ path: '/console/admin/users' }" title="Users" value="admin-users"></v-list-item>
                <v-list-item id="nav-admin-organizations" :to="{ path: '/console/admin/organizations' }" title="Organizations" value="admin-organizations"></v-list-item>
                    <v-list-group value="reporting">
                    <template v-slot:activator="{ props }">
                        <v-list-item v-bind="props" title="Reporting"></v-list-item>
                    </template>
                    <v-list-item id="nav-admin-reporting-ecosystem" :to="{ path: '/console/admin/reporting/ecosystem' }" title="Ecosystem" value="admin-reporting-ecosystem"></v-list-item>
                    <v-list-item id="nav-admin-reporting-users" :to="{ path: '/console/admin/reporting/users' }" title="Users" value="admin-reporting-users"></v-list-item>
                </v-list-group>
                <v-list-item id="nav-admin-sdk" :to="{ path: '/console/admin/sdk-release' }" title="SDK" value="admin-sdk"></v-list-item>
                <v-list-item id="nav-admin-special-files" :to="{ path: '/console/admin/special-files' }" title="Special Files" value="admin-special-files"></v-list-item>
                <v-list-item id="nav-admin-capture" :to="{ path: '/console/admin/capture-accounts' }" title="Capture Accounts" value="admin-capture-accounts"></v-list-item>
                <v-list-item id="nav-admin-quotes" :to="{ path: '/console/admin/quotes' }" title="Quotes" value="admin-quotes"></v-list-item>
                <v-list-item id="nav-admin-key-transfer" :to="{ path: '/console/admin/key-transfer' }" title="Key Transfer" value="admin-key-transfer"></v-list-item>
            </v-list-group>
        </template>
        <v-list-group value="support">
            <template v-slot:activator="{ props }">
            <v-list-item v-bind="props" prepend-icon="fas fa-headset" title="Get Support" value="support"></v-list-item>
            </template>
            <!-- <v-list-item :href="'https://developer.structure.io/docs/tutorials/tutorials'" target="_blank" title="Tutorials" value="tutorials"></v-list-item> -->
            <v-list-item :href="'https://support.structure.io/'" target="_blank" title="Support Articles" value="articles"></v-list-item>
            <v-list-item :href="'https://forums.structure.io'" target="_blank" title="Forums" value="forums"></v-list-item>
            <v-list-item :href="'https://structure.io/contact'" target="_blank" title="Contact Us" value="contact"></v-list-item>
        </v-list-group>
        </v-list>
    </v-navigation-drawer>
</template>